import { FC, ReactNode, useCallback } from 'react'
import DownloadIcon from 'ui/components/svg/DownloadIcon'
import { buildCsv, buildCsvFilename, downloadCsv } from '../../../utils/csv'
import {
  DownloadButton,
  RowCountLabel,
  StyledTable,
  TableFooter,
  TableTitle,
  TableViewport,
  TableWrapper
} from './nexaTable.styles'

export type NexaTableCell = string | number | boolean | null
export type NexaTableRow = NexaTableCell[]

interface NexaTableProps {
  headers: string[]
  rows: NexaTableRow[]
  title?: string
  // Optional per-cell renderer for the on-screen table. The CSV export always
  // uses the raw cell values, so renderCell is purely cosmetic.
  renderCell?: (value: NexaTableCell, columnIndex: number, rowIndex: number) => ReactNode
}

const defaultRenderCell = (value: NexaTableCell): ReactNode => {
  if (value === null || value === '') return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

const NexaTable: FC<NexaTableProps> = ({ headers, rows, title, renderCell }) => {
  const totalRows = rows.length
  const rowLabel = `${totalRows} ${totalRows === 1 ? 'row' : 'rows'}`

  const handleDownload = useCallback(() => {
    const csv = buildCsv(headers, rows)
    downloadCsv(buildCsvFilename(title), csv)
  }, [headers, rows, title])

  const cellRenderer = renderCell ?? defaultRenderCell

  return (
    <TableWrapper>
      {title && <TableTitle>{title}</TableTitle>}
      <TableViewport>
        <StyledTable>
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th key={index}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex}>{cellRenderer(cell, cellIndex, rowIndex)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableViewport>
      <TableFooter>
        <RowCountLabel>{rowLabel}</RowCountLabel>
        <DownloadButton type="button" onClick={handleDownload} aria-label="Download CSV">
          <DownloadIcon width={18} height={18} aria-hidden />
          Download CSV
        </DownloadButton>
      </TableFooter>
    </TableWrapper>
  )
}

export default NexaTable
